import { Link } from '@inertiajs/react'
import { CalendarDays, ChartColumnIncreasing, Check, Disc3, Megaphone, ShieldCheck, SplitSquareVertical } from 'lucide-react'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import LogoRail from '@/components/marketing/LogoRail'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface PlanCard {
  name: string
  thb_yearly: string
  thb_monthly: string
  usd_yearly: string
  usd_monthly: string
  royalty: string
  badge?: string
  description: string
  bullets: string[]
}

interface ThailandPageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    title: string
    body: string
    bg_image?: string
    primary_cta: { label: string; href: string }
    secondary_cta: { label: string; href: string }
    metrics: { value: string; label: string }[]
  }
  features: { title: string; body: string; icon: string }[]
  platform_logos: { name: string; src: string }[]
  artist_plans: PlanCard[]
  label_plans: PlanCard[]
  included_note: string
  artist_starting_price: string
  faq_items: { question: string; answer: string }[]
  cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
}

const iconMap = {
  calendar: CalendarDays,
  chart: ChartColumnIncreasing,
  disc: Disc3,
  megaphone: Megaphone,
  shield: ShieldCheck,
  split: SplitSquareVertical,
} as const

function PricingCard({ plan }: { plan: PlanCard }) {
  const isPro = plan.royalty === '100%'

  return (
    <div
      className={`group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border p-7 transition-all duration-200 hover:-translate-y-1 ${
        isPro
          ? 'border-[#ff6a1a]/30 bg-gradient-to-br from-[#ff6a1a]/10 to-[#ff6a1a]/[0.02] hover:border-[#ff6a1a]/50 hover:shadow-[0_20px_50px_rgba(255,106,26,0.12)]'
          : 'border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] hover:border-white/[0.16] hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]'
      }`}
    >
      {isPro && (
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#ff6a1a]/[0.08] blur-3xl" />
      )}

      {/* Header */}
      <div className="relative flex items-center justify-between">
        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
          {plan.name}
        </h3>
        {plan.badge ? (
          <span className="rounded-full bg-[#ff6a1a]/20 px-2.5 py-0.5 text-[0.65rem] font-bold text-[#ff6a1a]">
            {plan.badge}
          </span>
        ) : (
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
            isPro ? 'bg-[#ff6a1a]/20 text-[#ff6a1a]' : 'bg-white/[0.08] text-white/60'
          }`}>
            {plan.royalty}
          </span>
        )}
      </div>

      {/* Price */}
      <div className="relative mt-4">
        <p className="text-3xl font-bold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
          {plan.thb_yearly}
        </p>
        <p className="mt-0.5 text-sm text-white/48">หรือ {plan.thb_monthly}</p>
      </div>

      {/* Divider */}
      <div className="relative my-5 h-px bg-white/[0.06]" />

      {/* Description */}
      <p className="relative text-sm leading-relaxed text-white/60">{plan.description}</p>

      {/* Bullets */}
      <ul className="relative mt-5 flex-1 space-y-2.5">
        {plan.bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-2.5 text-sm text-white/72">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isPro ? 'text-[#ff6a1a]' : 'text-white/40'}`} />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href="/apply"
        className={`relative mt-6 block rounded-xl py-3 text-center text-sm font-bold transition-all duration-200 ${
          isPro
            ? 'bg-gradient-to-r from-[#ff7b2a] to-[#ff4d00] text-white shadow-[0_8px_24px_rgba(255,84,0,0.3)] hover:shadow-[0_12px_32px_rgba(255,84,0,0.4)]'
            : 'border border-white/[0.12] bg-white/[0.04] text-white hover:bg-white/[0.08]'
        }`}
      >
        สมัครเลย
      </a>
    </div>
  )
}

export default function Thailand({
  hero,
  features,
  platform_logos,
  artist_plans,
  label_plans,
  included_note,
  artist_starting_price,
  faq_items,
  cta,
}: ThailandPageProps) {
  return (
    <div lang="th">
      {/* Hero — same v2 layered treatment as the homepage. */}
      <section className="marketing-hero-section hero-v2 relative overflow-hidden" style={{ minHeight: '78vh' }}>
        {hero.bg_image && (
          <div className="hero-bg" style={{ backgroundImage: `url(${hero.bg_image})` }} />
        )}
        <div className="hero-fade" aria-hidden="true" />
        <div className="hero-spot-warm" aria-hidden="true" />
        <div className="hero-spot-cool" aria-hidden="true" />
        <div className="hero-bottom-fade" aria-hidden="true" />

        <div className="hero-content container relative z-10 mx-auto px-6 pb-8 pt-10 lg:pb-10 lg:pt-12">
          <div className="hero-text">
            <span className="hero-eyebrow">{hero.eyebrow}</span>

            {/* Thai-language h1 (page lang="th") reusing the title's own
                phrasing; visual slogan demoted to <p> — pixel-identical. */}
            <h1 className="sr-only">จัดจำหน่ายเพลงทั่วโลก สำหรับศิลปินไทย</h1>
            <p className="hero-headline">
              <span>Drop it.</span>
              <span className="grad-text">Own it.</span>
              <span>Get paid.</span>
            </p>

            <p className="hero-body">{hero.body}</p>

            <div className="hero-ctas">
              <a href={hero.primary_cta.href} className="marketing-button-primary">
                {hero.primary_cta.label}
              </a>
              <Link href={hero.secondary_cta.href} className="marketing-button-secondary" prefetch>
                {hero.secondary_cta.label}
              </Link>
            </div>
          </div>

          <div className="hero-stats">
            <div className="hero-stat-grid">
              {hero.metrics.map((metric) => (
                <div key={metric.label} className="hero-stat-card">
                  <span className="hero-stat-value">{metric.value}</span>
                  <span className="hero-stat-label">{metric.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features grid — cream slab to break the post-hero dark */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">สิ่งที่คุณจะได้รับ</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Distribution, analytics, splits, Content ID — one dashboard.</h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {features.map((item, index) => {
              const Icon = iconMap[item.icon as keyof typeof iconMap] ?? Disc3

              return (
                <ScrollReveal key={item.title} direction="up" delay={index * 0.08}>
                  <div className="marketing-light-card group h-full">
                    <div className="flex justify-end lg:justify-start">
                      <div className="marketing-icon-badge">
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-[#151515]">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#3b3b3b]">{item.body}</p>
                  </div>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Platforms + Pricing — merged dark zone, one warm glow, two stacked
          sub-blocks separated by space-y-16. */}
      <section className="marketing-section has-glow">
        <div className="section-glow warm" style={{ width: '38rem', height: '38rem', right: '-10rem', bottom: '-14rem' }} />
        <div className="container mx-auto px-6 space-y-20">
          <div>
            <ScrollReveal direction="up">
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">แพลตฟอร์มยอดนิยม</div>
                <h2 className="marketing-section-title">ปล่อยเพลงของคุณบน JOOX, Spotify, Apple Music และอีกกว่า 100 แพลตฟอร์ม</h2>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="fade">
              <LogoRail items={platform_logos} />
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div className="mt-8 flex justify-center">
                <Link href="/stores" className="marketing-button-secondary" prefetch>
                  See all stores →
                </Link>
              </div>
            </ScrollReveal>
          </div>

          <div className="space-y-16">
            <div>
              <ScrollReveal direction="up">
                <div className="marketing-section-header">
                  <div className="marketing-eyebrow-mono">แผนสำหรับศิลปิน</div>
                  <h2 className="marketing-section-title">เริ่มต้นเพียง {artist_starting_price} — ปล่อยเพลงได้ไม่จำกัด</h2>
                </div>
              </ScrollReveal>

              <div className="mx-auto grid max-w-3xl gap-5 sm:grid-cols-2">
                {artist_plans.map((plan, index) => (
                  <ScrollReveal key={plan.name} delay={index * 0.1}>
                    <PricingCard plan={plan} />
                  </ScrollReveal>
                ))}
              </div>
            </div>

            <div>
              <ScrollReveal direction="up">
                <div className="marketing-section-header">
                  <div className="marketing-eyebrow-mono">แผนสำหรับค่ายเพลง</div>
                  <h2 className="marketing-section-title">บริหารศิลปินไม่จำกัด จากแดชบอร์ดเดียว</h2>
                </div>
              </ScrollReveal>

              <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {label_plans.map((plan, index) => (
                  <ScrollReveal key={plan.name} delay={index * 0.1}>
                    <PricingCard plan={plan} />
                  </ScrollReveal>
                ))}
              </div>

              <ScrollReveal>
                <div className="mx-auto mt-10 max-w-3xl text-center">
                  <p className="text-sm text-white/56">{included_note}</p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — cream slab, breaks the dark Platforms+Pricing → CTA run
          so the page alternates D-C-D-C-D end-to-end. */}
      <section id="faq" className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">คำถามที่พบบ่อย</div>
              <h2 className="marketing-section-title marketing-section-title-dark">คำถามที่ศิลปินไทยถามก่อนตัดสินใจ</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="mx-auto max-w-4xl">
              <FaqAccordion items={faq_items} />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA — dark, matches the homepage and India send-off */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow-mono">เริ่มต้นเลย</div>
                <h2 className="marketing-section-title">{cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {cta.actions.map((action, index) => (
                  <a
                    key={action.label}
                    href={action.href}
                    className={index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}
                  >
                    {action.label}
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
